import React, { useState, useRef, useEffect } from 'react';
import { Icons } from '../../constants/icons';
import './InlineEditWidget.css';

interface InlineEditWidgetProps {
    value: string;
    onSave: (value: string) => Promise<void> | void;
    onEditStart?: () => void;
    onEditFinish?: () => void;
    onCancel?: () => void;
    placeholder?: string;
    className?: string;
    maxLength?: number;
    autoFocus?: boolean;
    onBlurSave?: (value: string) => Promise<void> | void;
    view?: (props: { value: string; onEditStart: () => void }) => React.ReactNode;
}

const InlineEditWidget: React.FC<InlineEditWidgetProps> = ({
	value,
	onSave,
	onEditStart,
	onEditFinish,
	onCancel,
	placeholder = 'Введите значение',
	className = '',
	maxLength = 255,
	autoFocus = true,
	view,
}) => {
	const [isEditing, setIsEditing] = useState(false);
	const [inputValue, setInputValue] = useState(value);
	const [isSaving, setIsSaving] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		setInputValue(value);
	}, [value]);

	useEffect(() => {
		if (isEditing && autoFocus && inputRef.current) {
			inputRef.current.focus();
			inputRef.current.select();
		}
	}, [isEditing, autoFocus]);

	const handleEditStart = () => {
		if (onEditStart) {
			onEditStart();
		}
		setIsEditing(true);
	};

	const handleSave = async () => {
		const trimmedValue = inputValue.trim();
		if (!trimmedValue) {
			// Не сохраняем пустые значения
			if (onCancel) {
				onCancel();
			}
			handleEditFinish();
			setIsEditing(false);
			return;
		}

		setIsSaving(true);
		try {
			await onSave(trimmedValue);
			handleEditFinish();
			setIsEditing(false);
		} catch (error) {
			console.error('Ошибка при сохранении:', error);
		} finally {
			setIsSaving(false);
		}
	};

	const handleCancel = () => {
		if (onCancel) {
			onCancel();
		}
		handleEditFinish();
		setIsEditing(false);
	};

	const handleEditFinish = () => {
		if (onEditFinish) {
			onEditFinish();
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSave();
		} else if (e.key === 'Escape') {
			e.preventDefault();
			handleCancel();
		}
	};

	const handleBlur = (e: React.FocusEvent) => {
		// Не закрываем при клике на кнопки внутри виджета
		if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget as Node)) {
			handleSave();
		}
	};

	if (!isEditing) {
		if (!view) return null;
		return (
			<div className={`inline-edit-widget ${className}`} onClick={()=>setIsEditing(true)}>
				{view({ value, onEditStart: handleEditStart })}
			</div>
		);
	}

	return (
		<div className={`inline-edit-widget ${className}`}>
			<div className="inline-edit-widget__input-container">
				<input
					ref={inputRef}
					type="text"
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
					onBlur={handleBlur}
					placeholder={placeholder}
					maxLength={maxLength}
					disabled={isSaving}
					className="inline-edit-widget__input"
					aria-label="Редактирование"
				/>
			</div>
			<div className="inline-edit-widget__buttons">
				<button
					type="button"
					onClick={handleSave}
					disabled={isSaving || !inputValue.trim()}
					className="inline-edit-widget__button inline-edit-widget__button--save"
					title="Сохранить"
					aria-label="Сохранить изменения"
				>
					{Icons.Checked}
				</button>
				<button
					type="button"
					onClick={handleCancel}
					disabled={isSaving}
					className="inline-edit-widget__button inline-edit-widget__button--cancel"
					title="Отменить"
					aria-label="Отменить изменения"
				>
					{Icons.Close}
				</button>
			</div>
		</div>
	);
};

export default InlineEditWidget;